// Getting two phones talking.
//
// The host runs the simulation and sends snapshots; the guest sends one
// direction. Everything after the handshake is peer to peer, so the server is
// not on the path of a chase and the app still idles at zero.
//
// ICE is NOT trickled. Gathering is allowed to finish before the offer or the
// answer is published, so each side posts one self-contained blob and there is
// no candidate exchange to keep alive. That costs a second or two at the start
// and removes most of the moving parts — a fair trade for two people in a room
// who are about to press Play anyway.

/**
 * Game codes. Must match server/duel.mjs, which generates them.
 *
 * No O/0 and no I/1: these get read aloud across a room, and a code you cannot
 * dictate is a code nobody joins.
 */
export const CODE_LEN = 4;
const NOT_CODE = /[^ABCDEFGHJKLMNPQRSTUVWXYZ23456789]/g;

/** Clean what somebody typed or pasted into something joinable. */
export function normaliseCode(input) {
  return String(input ?? '').toUpperCase().replace(NOT_CODE, '').slice(0, CODE_LEN);
}

/** True when a character was typed that a code can never contain. */
export const hasAmbiguous = (input) => /[O0I1]/.test(String(input ?? '').toUpperCase());

import { API_BASE } from '../core/native.js';

/**
 * Where the signalling lives.
 *
 * A native build is served from the Capacitor bundle, whose origin knows
 * nothing about /api/… — a root-absolute path there hits the SPA fallback and
 * comes back as index.html with a 200. `res.ok` is then true, `res.json()`
 * parses HTML, and WebKit throws "The string did not match the expected
 * pattern." from somewhere that mentions neither the app nor the network.
 * API_BASE is '' on the web and the deployment origin in an app.
 */
const api = (path) => `${API_BASE}${path}`;

/**
 * Read a JSON body, or fail with the same words as a flat refusal would.
 *
 * A 200 carrying HTML is not success — it means something other than the API
 * answered. Left to `res.json()` that surfaces as WebKit's "The string did not
 * match the expected pattern.", which sends you looking at the SDP.
 */
async function readJson(res, whenNot) {
  if (!(res.headers.get('content-type') || '').includes('json')) throw new Error(whenNot);
  try {
    return await res.json();
  } catch {
    throw new Error(whenNot);
  }
}

const STUN = [{ urls: 'stun:stun.l.google.com:19302' }];

/** Snapshots go out unreliably: a late one is worse than a lost one. */
const STATE_CHANNEL = { ordered: false, maxRetransmits: 0 };
/** The handshake and the inputs are small and must not be dropped. */
const CTRL_CHANNEL = { ordered: true };

const GATHER_TIMEOUT_MS = 4000;
const POLL_MS = 900;
const JOIN_TIMEOUT_MS = 90_000;

/**
 * Wait for ICE to finish, but not for ever.
 *
 * On a network where STUN is unreachable, gathering can hang until it times out
 * internally. The candidates already found are usually the local ones, which
 * are exactly the ones that matter on shared wifi — so publishing early is
 * better than not connecting at all.
 */
function gathered(pc) {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      pc.removeEventListener('icegatheringstatechange', check);
      clearTimeout(timer);
      resolve();
    };
    const check = () => { if (pc.iceGatheringState === 'complete') done(); };
    const timer = setTimeout(done, GATHER_TIMEOUT_MS);
    pc.addEventListener('icegatheringstatechange', check);
  });
}

const sdp = (d) => JSON.stringify({ type: d.type, sdp: d.sdp });

function peer(onFail) {
  const pc = new window.RTCPeerConnection({ iceServers: STUN });
  pc.addEventListener('connectionstatechange', () => {
    if (['failed', 'disconnected', 'closed'].includes(pc.connectionState)) onFail(pc.connectionState);
  });
  return pc;
}

/**
 * Open a game and wait for somebody to join.
 *
 * `onCode` fires as soon as there is a code to read out, which is well before
 * anyone connects — that is the whole point of it.
 */
export async function host({ onCode, onStatus, onCtrl, onClosed, signal }) {
  const say = (s) => onStatus?.(s);
  say('opening');
  const pc = peer((why) => { say(why === 'failed' ? 'failed' : 'closed'); onClosed?.(why); });
  const state = pc.createDataChannel('state', STATE_CHANNEL);
  const ctrl = pc.createDataChannel('ctrl', CTRL_CHANNEL);

  ctrl.addEventListener('message', (e) => {
    try { onCtrl?.(JSON.parse(e.data)); } catch { /* a malformed frame is not fatal */ }
  });

  await pc.setLocalDescription(await pc.createOffer());
  await gathered(pc);

  const res = await fetch(api('/api/duel'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ offer: sdp(pc.localDescription) }),
    signal,
  });
  if (!res.ok) { pc.close(); throw new Error('could not open a game'); }
  const { code } = await readJson(res, 'could not open a game');
  onCode?.(code);
  say('waiting');

  // Poll for the answer. Short and boring: the room lives in object storage so
  // the two phones do not have to reach the same server instance.
  const until = Date.now() + JOIN_TIMEOUT_MS;
  while (Date.now() < until) {
    if (signal?.aborted) { pc.close(); throw new Error('cancelled'); }
    const a = await fetch(api(`/api/duel/${code}/answer`), { signal }).catch(() => null);
    if (a && a.status === 200) {
      const { answer } = await readJson(a, 'lost contact with the server');
      say('connecting');
      await pc.setRemoteDescription(JSON.parse(answer));
      // BOTH channels. Waiting only for `state` was a real bug: the host sent
      // its hello the instant snapshots could flow, `ctrl` was still opening,
      // and sendCtrl silently dropped it — so the guest never learned the board
      // and threw away every snapshot that followed, with no error anywhere.
      const opened = (ch) => new Promise((resolve) => {
        if (ch.readyState === 'open') { resolve(true); return; }
        const t = setTimeout(() => resolve(false), 15_000);
        ch.addEventListener('open', () => { clearTimeout(t); resolve(true); }, { once: true });
      });
      const open = (await Promise.all([opened(state), opened(ctrl)])).every(Boolean);
      if (!open) { pc.close(); throw new Error('the connection did not open'); }
      say('connected');
      return {
        code,
        role: 'host',
        sendTick: (text) => { if (state.readyState === 'open') state.send(text); },
        sendCtrl: (text) => { if (ctrl.readyState === 'open') ctrl.send(text); },
        close: () => pc.close(),
      };
    }
    if (a && a.status === 404) { pc.close(); throw new Error('that game expired'); }
    await new Promise((r) => setTimeout(r, POLL_MS));
  }
  pc.close();
  throw new Error('nobody joined');
}

/** Join a game by its code. */
export async function join(code, { onStatus, onHello, onTick, onClosed, signal }) {
  const say = (s) => onStatus?.(s);
  say('connecting');
  const res = await fetch(api(`/api/duel/${encodeURIComponent(code)}`), { signal });
  if (res.status === 404) throw new Error('no game with that code');
  if (!res.ok) throw new Error('could not reach that game');
  const { offer } = await readJson(res, 'could not reach that game');

  const pc = peer((why) => { say(why === 'failed' ? 'failed' : 'closed'); onClosed?.(why); });
  let ctrl = null;
  let gotHello = false;
  const opened = new Promise((resolve) => {
    pc.addEventListener('datachannel', (e) => {
      const ch = e.channel;
      if (ch.label === 'ctrl') ctrl = ch;
      ch.addEventListener('message', (m) => {
        let msg;
        try { msg = JSON.parse(m.data); } catch { return; }
        if (msg.t === 'hello') { gotHello = true; onHello?.(msg); }
        else if (msg.t === 'tick') onTick?.(msg);
      });
      if (ch.label === 'state') {
        if (ch.readyState === 'open') resolve();
        else ch.addEventListener('open', resolve, { once: true });
      }
    });
  });

  await pc.setRemoteDescription(JSON.parse(offer));
  await pc.setLocalDescription(await pc.createAnswer());
  await gathered(pc);

  const post = await fetch(api(`/api/duel/${encodeURIComponent(code)}/answer`), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ answer: sdp(pc.localDescription) }),
    signal,
  });
  if (post.status === 409) { pc.close(); throw new Error('somebody already joined that game'); }
  if (!post.ok) { pc.close(); throw new Error('could not join that game'); }

  const ok = await Promise.race([
    opened.then(() => true),
    new Promise((r) => setTimeout(() => r(false), 20_000)),
  ]);
  if (!ok) { pc.close(); throw new Error('the connection did not open'); }
  // Ask for the board rather than assuming the hello was sent while this side
  // was listening. Repeats until it arrives, so neither a race nor a dropped
  // frame can leave a guest connected to a game it cannot draw.
  const askForBoard = setInterval(() => {
    if (gotHello) { clearInterval(askForBoard); return; }
    if (ctrl?.readyState === 'open') ctrl.send(JSON.stringify({ t: 'hi' }));
  }, 400);
  setTimeout(() => clearInterval(askForBoard), 20_000);

  say('connected');
  return {
    code,
    role: 'guest',
    sendCtrl: (text) => { if (ctrl?.readyState === 'open') ctrl.send(text); },
    close: () => { clearInterval(askForBoard); pc.close(); },
  };
}

export const supported = () => typeof window !== 'undefined' && !!window.RTCPeerConnection;
